import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl } from '@angular/forms';
import { BookingCancellationSourceModel, GroupClassDetailsModel, GroupClassParticipantsModel, ParticipantModel } from '@bayclubs/group-classes-employee-api';
import { ConfirmationService } from '@bayclubs/utils';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { gcManagementEnumerations } from 'src/app/interfaces/interfaces.module';
import { GroupClassesApiService } from 'src/app/shared/shared.module';
import { BookingEventService } from '../../../services/booking.service';
import { OrderEnum } from './OrderEnum';

const orderKey = "GroupClassesDetailsAttendensComponent.orderBy"

@Component({
    selector: '[app-group-classes-details-attendens]',
    templateUrl: './group-classes-details-attendens.component.html',
    styleUrls: ['./group-classes-details-attendens.component.scss'],
    standalone: false
})
export class GroupClassesDetailsAttendensComponent implements OnInit, OnChanges, OnDestroy {
    @Input()
    public model: GroupClassDetailsModel;
    public attendensModel: GroupClassParticipantsModel;

    @Output()
    public attendensLoaded: EventEmitter<GroupClassParticipantsModel> = new EventEmitter();


    orderByValues: {key: OrderEnum, value: string }[] = [
        {key: 'Booking Date', value: "By booking date"},
        {key: 'Alphabetically', value: "Alphabetically"}
    ]

    orderBy = new FormControl<OrderEnum>(localStorage.getItem(orderKey) as OrderEnum ?? 'Booking Date');

    private subscription: Subscription = new Subscription();

    private sortedParticipants = [];

    constructor(
        private api: GroupClassesApiService,
        private confirmation: ConfirmationService,
        private translateService: TranslateService,
        private bookingService: BookingEventService
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes['model'] && !!this.model) {
            this.loadParticipants();
        }
    }

    ngOnInit() {
        this.subscription.add(
            this.orderBy.valueChanges.subscribe(_ => {
                localStorage.setItem(orderKey, _)
                this.sortedParticipants = this.getSorted(this.attendensModel.items)
            })
        );
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    private loadParticipants(): void {
        this.api.participants(this.model.id).subscribe(result => {
            this.attendensModel = result;
            this.sortedParticipants = this.getSorted(result.items)
            this.attendensLoaded.emit(this.attendensModel);
        });
    }

    public getActive(): ParticipantModel[] {
        if (!this.sortedParticipants) {
            return [];
        }

        return this.sortedParticipants.filter(
            x => x.status.code === gcManagementEnumerations.GroupClassBookingStatus.active.code
        );
    }

    public getSorted(participants: ParticipantModel[]): ParticipantModel[] {
        const sortBy: OrderEnum = this.orderBy.value;

        switch (sortBy) {
            case 'Booking Date':
                return participants.slice().sort((a, b) => (a.bookedAt?.value > b.bookedAt?.value) ? 1 : -1)
            case 'Alphabetically':
                return participants.slice().sort((a, b) => (a.fullName > b.fullName) ? 1 : -1)
        }
    }

    public get cancelledBookingsExists(): boolean {
        return this.getCancelled().length > 0;
    }

    public getCancelled(): ParticipantModel[] {
        if (!this.attendensModel) {
            return [];
        }

        return this.attendensModel.items.filter(
            x => x.status.code === gcManagementEnumerations.GroupClassBookingStatus.cancelled.code
        );
    }

    public isMarkedAsNoShow(participant: ParticipantModel) {
        return (
            participant.presenceStatus &&
            participant.presenceStatus.code === gcManagementEnumerations.BookingPresenceStatus.noShow.code
        );
    }
    public cancelBooking(bookingId: string) {
        this.confirmation
            .show(
                this.translateService.instant('GroupClassDetails.CancelBookingConfirmationTitle'),
                this.translateService.instant('GroupClassDetails.CancelBookingConfirmationText'),
                this.translateService.instant('General.Confirm'),
                this.translateService.instant('General.Dismiss')
            )
            .subscribe(result => {
                if (result) {
                    this.api.cancelBooking(bookingId).subscribe(data => {
                        this.loadParticipants();
                        this.bookingService.bookingCancelled(bookingId);
                    });
                }
            });
    }

    public showCancelledBy(participant: ParticipantModel){
        return participant.cancellationSource === BookingCancellationSourceModel.Employee ||
            participant.cancellationSource === BookingCancellationSourceModel.ResourceDeactivation;
    }

}
